import type { ChatAppResponse, WorkflowItem } from "@/api/models"
import type { FlowRun } from "@/features/workflow-editor/hooks/use-workflow-runs"

import { splitDenialMarker } from "./workflow-run-denial-utils"

type WorkflowRunChatInput = Pick<FlowRun, "id" | "output">
type WorkflowRunStatusChatInput = Pick<
  FlowRun,
  "id" | "status" | "output" | "error" | "pending_interrupt"
> &
  Partial<Pick<FlowRun, "workflow_id" | "workflow_title" | "trigger">>
type WorkflowRunRequestErrorInput = {
  workflowId: string
  workflowTitle: string
  error: unknown
}

type PersistWorkflowRunStatusInput = {
  sessionId: string
  userText: string
  run: WorkflowRunStatusChatInput
  saveWorkflowRunMessage: (
    sessionId: string,
    question: string,
    response: ChatAppResponse
  ) => Promise<unknown>
}

const emptyFlowOutputMessage = "Flow completed with no output."
const deniedFlowRunMessage = "Flow run was denied."
const activeFlowRunMessages: Record<"queued" | "running", string> = {
  queued: "Flow run is queued. Open run history to inspect progress.",
  running: "Flow run is still running. Open run history to inspect progress.",
}

function getWorkflowRunMetadata(run: WorkflowRunStatusChatInput): ChatAppResponse["workflow_run"] {
  if (!run.workflow_id) return undefined
  return {
    id: run.id,
    workflow_id: run.workflow_id,
    workflow_title: run.workflow_title,
    status: run.status,
    trigger: run.trigger,
    output: run.output,
    error: run.error,
    pending_interrupt: run.pending_interrupt,
  }
}

function withWorkflowRunMetadata(
  response: ChatAppResponse,
  run: WorkflowRunStatusChatInput
): ChatAppResponse {
  const workflowRun = getWorkflowRunMetadata(run)
  if (!workflowRun) return response

  return {
    ...response,
    context: {
      ...response.context,
      workflow_run: workflowRun,
    },
    workflow_run: workflowRun,
  }
}

function stringifyWorkflowOutput(output: unknown): string {
  if (typeof output === "string") {
    return output.trim()
  }
  if (output == null) {
    return ""
  }
  try {
    return JSON.stringify(output, null, 2)
  } catch {
    return String(output)
  }
}

export function getWorkflowRunChatQuestion(
  workflow: Pick<WorkflowItem, "title">,
  question = ""
): string {
  const trimmedQuestion = question.trim()
  return trimmedQuestion || `Run ${workflow.title}`
}

export function getWorkflowRunChatContent(run: WorkflowRunChatInput): string {
  const content = stringifyWorkflowOutput(run.output)
  // The approval-denied marker is for the UI to read, not to show: the run
  // card below the message carries the Denied badge and the reason (#2998).
  // This text is persisted with the session, so it is stripped before saving.
  const denial = splitDenialMarker(content)
  if (denial.denied) return denial.output || deniedFlowRunMessage
  return content || emptyFlowOutputMessage
}

export function createWorkflowRunChatResponse(run: WorkflowRunChatInput): ChatAppResponse {
  return {
    content: getWorkflowRunChatContent(run),
    context: {
      data_points: [],
      followup_questions: null,
      thoughts: [],
    },
    session_state: null,
    completed: true,
    messageId: `flow-run:${run.id}`,
  }
}

export function createWorkflowRunPendingChatResponse(
  workflow: Pick<WorkflowItem, "id" | "title">
): ChatAppResponse {
  const workflowRun = {
    id: `pending:${workflow.id}`,
    workflow_id: workflow.id,
    workflow_title: workflow.title,
    status: "running" as const,
    trigger: "manual" as const,
    output: "",
  }

  return {
    content: "Flow run started. Waiting for the workflow to finish...",
    context: {
      data_points: [],
      followup_questions: null,
      thoughts: [],
      workflow_run: workflowRun,
    },
    session_state: null,
    completed: false,
    messageId: `flow-run-pending:${workflow.id}`,
    workflow_run: workflowRun,
  }
}

export function createWorkflowRunStatusChatResponse(
  run: WorkflowRunStatusChatInput
): ChatAppResponse {
  if (run.status === "queued" || run.status === "running") {
    return withWorkflowRunMetadata(
      {
        content: activeFlowRunMessages[run.status],
        context: {
          data_points: [],
          followup_questions: null,
          thoughts: [],
        },
        session_state: null,
        completed: true,
        messageId: `flow-run:${run.id}`,
      },
      run
    )
  }

  if (run.status === "failed") {
    return withWorkflowRunMetadata(
      {
        content: `Flow run failed${run.error ? `: ${run.error}` : "."}`,
        context: {
          data_points: [],
          followup_questions: null,
          thoughts: [],
        },
        session_state: null,
        completed: true,
        error: run.error ?? "Flow run failed",
        messageId: `flow-run:${run.id}`,
      },
      run
    )
  }

  if (run.status === "paused") {
    const prompt = run.pending_interrupt?.prompt || "Awaiting human input to continue."
    return withWorkflowRunMetadata(
      {
        content: `Flow run paused: ${prompt}`,
        context: {
          data_points: [],
          followup_questions: null,
          thoughts: [],
        },
        session_state: null,
        completed: true,
        messageId: `flow-run:${run.id}`,
      },
      run
    )
  }

  return withWorkflowRunMetadata(createWorkflowRunChatResponse(run), run)
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || "Unknown error")
}

export function createWorkflowRunRequestErrorChatResponse({
  workflowId,
  workflowTitle,
  error,
}: WorkflowRunRequestErrorInput): ChatAppResponse {
  const errorMessage = getErrorMessage(error)
  return {
    content: `Flow run request failed for ${workflowTitle}: ${errorMessage}`,
    context: {
      data_points: [],
      followup_questions: null,
      thoughts: [],
    },
    session_state: null,
    completed: true,
    error: errorMessage,
    messageId: `flow-run-request-error:${workflowId}:${Date.now()}`,
  }
}

export async function persistWorkflowRunStatusChatResponse({
  sessionId,
  userText,
  run,
  saveWorkflowRunMessage,
}: PersistWorkflowRunStatusInput): Promise<ChatAppResponse> {
  const response = createWorkflowRunStatusChatResponse(run)
  await saveWorkflowRunMessage(sessionId, userText, response)
  return response
}

/**
 * The backend's `{"error": "..."}` reason from a failed request, when the
 * response carried one (#2964). A resume the route refuses up front ("Run is
 * already being resumed", "Run is not paused or failed", ...) answers with
 * that shape; without this the card only had axios's "Request failed with
 * status code 400" to show.
 */
export function getApiErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (!data || typeof data !== "object") return null
  const message = (data as { error?: unknown }).error
  return typeof message === "string" && message.trim().length > 0 ? message : null
}

export function getWorkflowRunFromApiError(error: unknown): FlowRun | null {
  const data = (error as { response?: { data?: unknown } }).response?.data
  if (!data || typeof data !== "object") return null

  const candidate = data as Partial<FlowRun>
  if (
    typeof candidate.id === "string" &&
    typeof candidate.workflow_id === "string" &&
    typeof candidate.status === "string" &&
    "output" in candidate
  ) {
    return candidate as FlowRun
  }
  return null
}
